import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import toast from 'react-hot-toast';
import { 
  useTheme, 
  useMediaQuery, 
  Box,   
  Typography, 
  CircularProgress, 
  Tooltip, 
  IconButton, 
} from '@mui/material';
import { 
  AddCircleOutline, 
  Edit,
  Delete,
  DeleteForever,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import api from 'state/axios';
import { LAYOUT_HEIGHT } from 'state/constants';
import { hasPermission } from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import DataGridComponent from 'components/DataGridComponent';
import CardList from 'components/CardList';


const Holidays = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const canCreate = hasPermission(user?.permissions, "Holidays", "create", false);
  const canEdit   = hasPermission(user?.permissions, "Holidays", "update", false);
  const canDelete = hasPermission(user?.permissions, "Holidays", "delete", false);  

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null); // 'single' or 'bulk'
  const [idToDelete, setIdToDelete] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });

  const [holidays, setHolidays] = useState([]);


  useEffect(() => {
    const handleHolidays = async () => {
      try {
        setError(null);
        setLoading(true);  

        const res = await api.get('/holidays');  
        setHolidays(res.data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };
  
    handleHolidays();
  }, []); 

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,      
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      ...(() => {
        return isMobile ? { width: 60 } : { flex: 1 };
      })(),
    },
    { 
      field: "holidayName", 
      headerName: "HolidayName", 
      ...(() => {
        return isMobile ? { width: 250 } : { flex: 3 };
      })(),
    },
    { 
      field: "frequency", 
      headerName: "Frequency", 
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),
    },
    {
      field: "startDate",
      headerName: "StartDate",
      valueFormatter: (value) => {
        if (value == null) {
          return '';
        }
        return `${format(value, "dd/MM/yyyy")}`;
      },
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),
    },
    {
      field: "endDate",
      headerName: "EndDate",
      valueFormatter: (value) => {
        if (value == null) {
          return '';
        }
        return `${format(value, "dd/MM/yyyy")}`;
      },
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),
    },
    {
      field: "description",
      headerName: "Description",
      ...(() => {
        return isMobile ? { width: 300 } : { flex: 4 };
      })(),
    },
    {
      field: "isLunar",
      headerName: "IsLunar",
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),
    },
    {
      field: "isActive",
      headerName: "IsActive",
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),
    },
  ], [isMobile]);

  const actionColumn = useMemo(() => {    
    const activeCount = [canEdit, canDelete].filter(Boolean).length;
    if (activeCount === 0) return [];

    const baseWidth = 60 + activeCount * 25;

    return [
      {
        field: "action",
        headerName: "Action",
        filterable: false,
        sortable: false,
        width: baseWidth,
        renderCell: (params) => {
          return (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={1}
              sx={{ width: "100%", height: "100%" }}
            >
              {canEdit && (
                <Tooltip title="Edit">
                  <IconButton
                    aria-label="Edit"
                    sx={{
                      color: theme.palette.primary.dark,
                      "&:hover": { color: theme.palette.green.deep },
                    }}
                    onClick={() => {
                      if (!canEdit) return;
                      navigate(`/holidays/${params.row._id}`)
                    }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip title="Delete">
                  <IconButton
                    aria-label="Delete"
                    sx={{
                      color: theme.palette.primary.dark,
                      "&:hover": { color: theme.palette.green.deep },
                    }}
                    onClick={() => {
                      if (!canDelete) return;
                      handleDeleteClick(params.row._id);                  
                    }}
                  >
                    <DeleteForever />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
    ];
  }, [
    navigate, 
    theme.palette, 
    canEdit,
    canDelete,
  ]);
    
  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, holidays);    
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }
    
    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {
      if (!canDelete) return;

      if (deleteType === 'single') {
        if (!idToDelete) return;
        
        await api.delete(`/holidays/delete/${idToDelete}`);

        setHolidays(holidays.filter((item) => item._id !== idToDelete));
        toast.success('Holiday deleted successfully.');
      } else if (deleteType === 'bulk') {
        const selectedIds = getSelectedIds(rowSelectionModel, holidays);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }

        const url = '/holidays/soft-delete';
        const payload = { ids: selectedIds };        
        const res = await api.patch(url, payload);        
        toast.success(res.data?.message || `${selectedIds.length} record(s) deleted successfully.`);
        
        setHolidays(holidays.filter((item) => !selectedIds.includes(item._id)));
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete holidays!");
    }
  }, [
    canDelete, 
    deleteType, 
    idToDelete, 
    rowSelectionModel, 
    holidays,
  ]);
 

  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Holidays" 
          subtitle={`${loading ? 'Loading...' : 'List of Holidays'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            {!isMobile && canDelete && (
              <Tooltip title="Bulk soft delete" arrow>
                <span>
                  <StyledButton
                    disabled={!isAnyRowSelected(rowSelectionModel, holidays)}
                    onClick={handleBulkDelete}
                  >
                    <Delete />
                  </StyledButton>
                </span>
              </Tooltip>
            )}

            {canCreate && (
              <Tooltip title="Create" arrow>
                <span>
                  <StyledButton onClick={() => navigate("/holidays/new")}>
                    <AddCircleOutline />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          sx={{
            "& > div": { gridColumn: isDesktop ? undefined : "span 12" },
          }}
        >
          <Box
            height={LAYOUT_HEIGHT}
            sx={{ gridColumn: "span 12" }}
          >
            {isMobile ? (
              <CardList
                rows={holidays}
                primaryField="holidayName"
                fields={[
                  { label: "Frequency", key: "frequency" },
                  { label: "Start Date", key: "startDate" },
                  { label: "End Date", key: "endDate" },
                  { label: "Description", key: "description" },
                  { label: "Is Lunar", key: "isLunar" },
                ]}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={(id) => navigate(`/holidays/${id}`)}
                onDelete={handleDeleteClick}
              />
            ) : (
            <DataGridComponent
              rows={holidays || []}
              columns={columns.concat(actionColumn)}
              columnVisibilityModel={columnVisibilityModel}
              setColumnVisibilityModel={setColumnVisibilityModel}
              loading={loading}
              props={{
                checkboxSelection: true,
                disableRowSelectionOnClick: true,
                onRowSelectionModelChange: setRowSelectionModel,
                rowSelectionModel,
                getRowClassName: (params) => params.row.isActive === false ? 'isActive-false' : '',
                initialState: { pagination: { paginationModel: { pageSize: 100 } } },
                sx: {
                  "& .MuiDataGrid-row.isActive-false": {
                    backgroundColor: `${theme.palette.primary.light} !important`,  
                  },
                },
              }}
            />
            )}
          </Box>
        </Box>
      )}

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default Holidays;
